import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import CustomTooltip from "../../components/CustomTooltip";
import { LoadingButton } from "@mui/lab";
import { Log } from "../../services/services";
import api from "../../services/api";
import { showSuccessMsg, showErrorMsg } from "../../utils/toast";
import { styles } from "../../utils/styles";
import { acquirerList } from "../../utils/constants";

const formattedItems = [...acquirerList]
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((item) => ({
    id: item.id,
    label: item.name,
  }));

const PatchDataManual = () => {
  const [acquirer, setAcquirer] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [baseRate, setBaseRate] = useState("");
  const [markupRate, setMarkupRate] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDialogConfirmTrigger, setOpenConfirmTriggerDialog] = useState(false);

  const isFormValid = acquirer && transactionId && baseRate !== "" && markupRate !== "";

  const handleCloseConfirmTriggerDialog = () => {
    setOpenConfirmTriggerDialog(false);
  };

  const handleOpenConfirmTriggerDialog = () => {
    if (!isFormValid) {
      showErrorMsg("Please fill in all required fields.");
      return;
    }
    setOpenConfirmTriggerDialog(true);
  };

  const onSubmit = async () => {
    handleCloseConfirmTriggerDialog();
    setLoading(true);

    try {

      const patchPayload = {
        trigger_type: "PatchMDR",
        acquirer: acquirer,
        transaction_id: transactionId,
        base_rate: parseFloat(baseRate),
        markup_rate: parseFloat(markupRate)
      };

      Log.info("Triggering PatchMDR via Orchestrator API", patchPayload);
      
      const response = await api.post("/payout/ingestor/patch_rate/", patchPayload);
      
      // Due to api.js interceptor returning Promise.resolve(err.response), 
      // we must check the status manually.
      if (response && (response.status === 200 || response.status === 201)) {
        showSuccessMsg("Patch Data triggered successfully");
      } else {
        const errorMsg = response?.data?.detail || 
          (typeof response?.data === "string" ? response.data : (response?.data ? JSON.stringify(response.data) : "")) || 
          "An error occurred during patch trigger";
        showErrorMsg(errorMsg);
        Log.error("PatchData API Error", response);
      }

    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "An unexpected error occurred.";
      showErrorMsg(errorMsg);
      Log.error("PatchData Exception", error);
    } finally {
      setLoading(false);
    }
  };

  const patchRateReminder = (
    <Box sx={{ pl: 1 }}>
      <Typography component="li" sx={{ fontSize: "0.875rem", fontWeight: 400 }}>Make sure the <b>Acquirer</b> and <b>Transaction ID</b> are correct.</Typography>
      <Typography component="li" sx={{ fontSize: "0.875rem", fontWeight: 400 }}>Only submit if the new Base or Markup rates are different from the current ones.</Typography>
      <Typography component="li" sx={{ fontSize: "0.875rem", fontWeight: 400 }}>Transactions that are already <b>reconciled</b> or <b>settled</b> cannot be patched.</Typography>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Patch Rate (MDR)</Typography>
        <CustomTooltip content={patchRateReminder} />
      </Box>

      <Box sx={{ width: 250 }}>
        <FormControl fullWidth sx={{ my: 2 }}>
          <Autocomplete
            options={formattedItems}
            value={formattedItems.find((item) => item.id === acquirer) || null}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  {option.label}
                </li>
              );
            }}
            onChange={(e, newValue) => {
              setAcquirer(newValue?.id || "");
            }}
            fullWidth
            renderInput={(params) => (
              <TextField {...params} label="Acquirer" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </FormControl>

        <FormControl fullWidth sx={{ my: 2 }}>
          <TextField
            label="Transaction ID"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            fullWidth
          />
        </FormControl>

        <FormControl fullWidth sx={{ my: 2 }}>
          <TextField
            label="Base Rate (%)"
            type="number"
            value={baseRate}
            onChange={(e) => setBaseRate(e.target.value)}
            fullWidth
          />
        </FormControl>

        <FormControl fullWidth sx={{ my: 2 }}>
          <TextField
            label="Markup Rate (%)"
            type="number"
            value={markupRate}
            onChange={(e) => setMarkupRate(e.target.value)}
            fullWidth
          />
        </FormControl>
      </Box>

      <Box sx={{ mt: 5 }}>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={!isFormValid || loading}
          onClick={handleOpenConfirmTriggerDialog}
          sx={{ ...styles.customButton }}
        >
          Submit
        </LoadingButton>
      </Box>

      <Dialog
        open={openDialogConfirmTrigger}
        onClose={handleCloseConfirmTriggerDialog}
      >
        <DialogTitle>
          Are you sure you want to patch MDR for transaction <b>{transactionId}</b>?
        </DialogTitle>
        <DialogActions>
          <LoadingButton
            variant="outlined"
            onClick={handleCloseConfirmTriggerDialog}
          >
            Cancel
          </LoadingButton>
          <LoadingButton 
            variant="contained" 
            color="info" 
            onClick={onSubmit} 
            sx={{ ...styles.customButton }}
            loading={loading}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatchDataManual;
